import { useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { useAuth } from '../contexts/AuthContext.jsx'
import ListGrid, { LIST_VIEWS, listViewLabel } from '../components/ListGrid.jsx'
import GlyphTile from '../components/GlyphTile.jsx'
import Icon from '../components/Icon.jsx'
import { lucideGlyph } from '../components/LucideIcon.jsx'
import { PrintShell } from '../components/PrintDialog.jsx'
import { DateField, DayNav } from '../components/DatePicker.jsx'
import { useToast, useUndo } from '../components/Toast.jsx'
import { listIdFromParam, listPath, slugify } from '../lib/lists.js'
import { readJSON, writeJSON } from '../lib/localStore.js'
import {
  addDays,
  addMonths,
  formatDate,
  formatMonth,
  lastOfMonth,
  monthGrid,
  monthOf,
  startOfWeek,
} from '../lib/dates.js'

// The date and the view live in the URL while you are on the page, the same way the board's
// do, so a list is a link somebody can hand over; localStorage underneath is what a bare
// visit from the nav restores. One memory for every list rather than one per list: somebody
// keeping three lists is looking at the same week on all three.
const VIEW_KEY = 'fgs-list-view'
const DATE_KEY = 'fgs-list-date'

/** Seven days from a week's first day. Whole weeks: a list is not bound to working days. */
const weekFrom = (start) => Array.from({ length: 7 }, (_, i) => addDays(start, i))

export default function ListBoard() {
  const { slug } = useParams()
  const id = listIdFromParam(slug)
  const navigate = useNavigate()
  const { tenant, mode: currentMode, can } = useAuth()
  const qc = useQueryClient()
  const toast = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const today = currentMode?.today
  const [date, setDate] = useState(() => searchParams.get('d') || readJSON(DATE_KEY, null) || today)
  const [view, setView] = useState(() => {
    // From a URL somebody may have typed, so validated rather than trusted.
    const wanted = searchParams.get('v') || readJSON(VIEW_KEY, 'week')
    return LIST_VIEWS.includes(wanted) ? wanted : 'week'
  })
  const [clip, setClip] = useState(null) // { userId, name, note, cutEntry } | null
  const [printing, setPrinting] = useState(false) // the print/copy/save dialog

  useEffect(() => {
    writeJSON(DATE_KEY, date)
    writeJSON(VIEW_KEY, view)
    const next = new URLSearchParams(searchParams)
    next.set('d', date)
    next.set('v', view)
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, view])

  // The control header is sticky (see .board-page .page-head); publishing its measured
  // height is what lets the grid size itself to what is left of the window — the same
  // arrangement Board.jsx makes, for the same reason.
  const headRef = useRef(null)
  const pageRef = useRef(null)
  useEffect(() => {
    const head = headRef.current
    const page = pageRef.current
    if (!head || !page) return undefined
    const publish = () =>
      page.style.setProperty('--board-head-h', `${Math.round(head.getBoundingClientRect().height)}px`)
    publish()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null
    ro?.observe(head)
    window.addEventListener('resize', publish)
    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', publish)
    }
  }, [date])

  const weekStart = tenant?.weekStart || 1
  // Rows of days. The four views are the same grid with a different number of rows — see
  // ListGrid.jsx — so this is the whole of what tells them apart.
  const weeks = useMemo(() => {
    if (!date) return []
    if (view === 'day') return [[date]]
    const start = startOfWeek(date, weekStart)
    if (view === 'week') return [weekFrom(start)]
    if (view === '2week') return [weekFrom(start), weekFrom(addDays(start, 7))]
    const grid = monthGrid(monthOf(date), weekStart)
    const rows = []
    for (let i = 0; i < grid.length; i += 7) rows.push(grid.slice(i, i + 7))
    return rows
  }, [date, view, weekStart])
  const from = weeks[0]?.[0]
  const lastRow = weeks[weeks.length - 1]
  const to = lastRow?.[lastRow.length - 1]

  const { data: board, isLoading, error } = useQuery({
    queryKey: ['list-board', id, from, to],
    queryFn: () => api.get(`/api/lists/${id}/board?from=${from}&to=${to}`),
    enabled: Boolean(id && from && to),
    // Other people's edits arrive over the live channel (lib/useRealtime.js); this is the
    // backstop for the case that channel is down.
    refetchInterval: 90_000,
  })

  // The address carries the name for the person reading it and the id for the lookup, so a
  // renamed list's old link still lands — and is quietly corrected to the current name.
  useEffect(() => {
    if (!board?.list) return
    const canonical = `${slugify(board.list.name)}-${board.list.id}`
    if (slug !== canonical) navigate({ pathname: listPath(board.list), search: window.location.search }, { replace: true })
  }, [board?.list?.name, board?.list?.id, slug, navigate])

  const invalidate = () => qc.invalidateQueries({ queryKey: ['list-board'] })
  // "Removed. Undo" — re-adding through the same endpoint a fresh add uses, so it meets every
  // rule the original did; see useUndo.
  const undo = useUndo(invalidate)

  const entriesPath = `/api/lists/${id}/entries`
  const nameOf = (entry) => entry.userName || board?.people?.find((p) => p.id === entry.userId)?.name || 'That person'

  // Every handler returns whether the write landed: the grid's editor closes on a yes and
  // stays open, with the reason beside it, on a no.
  async function addEntry({ date: on, userId, note }) {
    try {
      await api.post(entriesPath, { date: on, userId, note })
      invalidate()
      return true
    } catch (err) {
      toast.error(err)
      return false
    }
  }

  async function saveNote(entry, note) {
    try {
      await api.patch(`${entriesPath}/${entry.id}`, { note })
      invalidate()
      return true
    } catch (err) {
      toast.error(err)
      return false
    }
  }

  async function moveEntry(entry, toDate) {
    if (entry.date === toDate) return true
    try {
      await api.patch(`${entriesPath}/${entry.id}`, { date: toDate })
      invalidate()
      // Said out loud: a drop that lands one day off looks identical to one that landed
      // right, and naming the day is how that gets caught.
      toast.success(`${nameOf(entry)} moved to ${formatDate(toDate)}.`)
      return true
    } catch (err) {
      toast.error(err)
      return false
    }
  }

  async function copyEntry(entry, toDate) {
    try {
      await api.post(entriesPath, { date: toDate, userId: entry.userId, note: entry.note || '' })
      invalidate()
      toast.success(`${nameOf(entry)} added to ${formatDate(toDate)}.`)
      return true
    } catch (err) {
      toast.error(err)
      return false
    }
  }

  async function removeEntry(entry) {
    try {
      await api.del(`${entriesPath}/${entry.id}`)
      invalidate()
      undo(`Removed ${nameOf(entry)} from ${formatDate(entry.date)}.`, () =>
        api.post(entriesPath, { date: entry.date, userId: entry.userId, note: entry.note || '' }),
      )
      return true
    } catch (err) {
      toast.error(err)
      return false
    }
  }

  // Escape clears a pending copy or cut — the same key every other menu and dialog uses to
  // back out of something.
  useEffect(() => {
    if (!clip) return undefined
    const onKey = (e) => { if (e.key === 'Escape') setClip(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [clip])

  if (!id) return <Navigate to="/lists" replace />
  if (!date) return <div className="page empty">Loading…</div>

  const list = board?.list
  const Glyph = list ? lucideGlyph(list.icon) : null
  const isMonth = view === 'month'
  const pageStep = view === '2week' ? 14 : view === 'week' ? 7 : 1
  // A month is not a number of days — stepping one by adding 30 lands in the wrong month
  // twice a year — so it steps itself, clamped to the target month's length.
  const stepMonth = (d, dir) => {
    const ym = addMonths(monthOf(d), dir)
    const last = lastOfMonth(ym)
    const wanted = `${ym}-${d.slice(8)}`
    return wanted > last ? last : wanted
  }
  const when =
    view === 'day'
      ? formatDate(date, { withYear: true })
      : isMonth
        ? formatMonth(monthOf(date))
        : view === '2week'
          ? `${formatDate(from)} – ${formatDate(to)}`
          : `Week of ${formatDate(from)}`

  return (
    <div className="page board-page list-page" ref={pageRef}>
      <div className="page-head board-head no-print" ref={headRef}>
        <div className="board-head-title head-titled">
          {list && <GlyphTile icon={Glyph} tint={list.color} size={36} />}
          <div className="head-titled-text">
            <h1 className="list-head-name">
              {list?.name || 'List'}
              {list && !list.active && <span className="tag muted">Retired</span>}
            </h1>
            <p className="board-head-when">
              {when}
              {list?.description && <span className="list-head-desc">{list.description}</span>}
            </p>
          </div>
        </div>

        <div className="spacer" />

        {/* No list picker here any more: the nav carries one, and it names the board you
            are on — so this was a second copy of the same control, directly beneath the
            first, offering the same choices. Losing it also makes this header the board's
            header exactly: a tile, a name, the period, then the date controls. */}
        <div className="board-head-group">
          <DayNav
            date={date}
            setDate={setDate}
            today={today}
            step={pageStep}
            stepBy={isMonth ? stepMonth : undefined}
          />
          <DateField value={date} onChange={setDate} today={today} weekStart={weekStart} label="List date" />
          <div className="segmented">
            {LIST_VIEWS.map((v) => (
              <button key={v} className={view === v ? 'selected' : ''} onClick={() => setView(v)}>
                {listViewLabel(v)}
              </button>
            ))}
          </div>
        </div>

        <span className="board-head-divider" aria-hidden="true" />

        {/* On every view, unlike the board's — a list has no month it cannot honestly print,
            since all four of its views draw the same thing at different lengths. Plain rather
            than the board's split button: there is nothing else to put in a menu behind it. */}
        <div className="board-head-group">
          <button
            className="btn primary"
            onClick={() => setPrinting(true)}
            disabled={!board}
            title={board ? undefined : 'Nothing to print yet'}
          >
            <Icon name="printer" size={16} /> Print
          </button>
        </div>
      </div>

      {clip && (
        <div className="notice no-print list-clip-notice">
          <Icon name={clip.cutEntry ? 'cut' : 'copy'} size={14} />
          <span>
            {clip.cutEntry ? 'Cut' : 'Copied'} <strong>{clip.name}</strong>
            {clip.note ? ` — ${clip.note}` : ''}. Right-click a day to paste, or press Escape.
          </span>
          <button className="btn sm ghost" onClick={() => setClip(null)}>Cancel</button>
        </div>
      )}

      {board && !board.canWrite && (
        <div className="notice no-print">
          {list && !list.active
            ? 'This list has been retired. It can still be read; an owner can bring it back under Settings → Lists.'
            : 'You have read-only access to this list.'}
        </div>
      )}

      {error ? (
        <div className="card empty">{error.message}</div>
      ) : isLoading || !board ? (
        <div className="empty">Loading list…</div>
      ) : board.people.length === 0 && board.entries.length === 0 ? (
        <div className="card empty">Nobody to put on a list yet. Add people under Settings → User Management.</div>
      ) : (
        <ListGrid
          board={board}
          weeks={weeks}
          view={view}
          today={today}
          month={isMonth ? monthOf(date) : null}
          readOnly={!can?.writeLists}
          clip={clip}
          onCopyEntry={setClip}
          onAdd={addEntry}
          onSaveNote={saveNote}
          onMove={moveEntry}
          onCopy={copyEntry}
          onRemove={removeEntry}
          onPickDay={view === 'day' ? null : (d) => { setDate(d); setView('day') }}
        />
      )}

      {/* The same three outputs the board offers, off the same off-screen stage — see
          PrintShell. The grid is handed `readOnly`, which is what strips the hover chrome,
          the add buttons and the drag handles; nothing else about it changes, so what comes
          off the printer is the view being pointed at. */}
      {printing && board && (
        <PrintShell
          modalTitle="Print list"
          title={list?.name || 'List'}
          subtitle={<span>{when}</span>}
          fileName={`${slugify(tenant?.name || 'list')}-${slugify(list?.name || 'list')}-${
            view === 'day' ? date : isMonth ? monthOf(date) : `${from}_${to}`
          }.png`}
          onClose={() => setPrinting(false)}
        >
          <ListGrid
            board={board}
            weeks={weeks}
            view={view}
            today={today}
            month={isMonth ? monthOf(date) : null}
            readOnly
          />
        </PrintShell>
      )}
    </div>
  )
}
